import React from 'react';

export const useInteractOutside = <ElementType,>(
	onInteractOutside: (event: FocusEvent) => void,
) => {
	const componentRef = React.useRef<ElementType>(null);

	React.useEffect(() => {
		const onInteract = (event: FocusEvent) => {
			if (!componentRef.current) return;

			if (
				(componentRef.current as unknown as Node).contains(event.target as Node)
			)
				return;

			onInteractOutside(event);
		};
		document.addEventListener('focusin', onInteract);
		document.addEventListener('click', onInteract);

		return () => {
			document.removeEventListener('focusin', onInteract);
			document.removeEventListener('click', onInteract);
		};
	}, [onInteractOutside]);

	return {
		ref: componentRef,
		// So focus doesn't go outside when clicking inside
		tabIndex: -1,
	};
};

/**
 * A utility function to handle interactions outside of a specific element.
 * It listens for focus and click events and calls the provided callback
 * if the interaction occurs outside the specified element.
 *
 * @example
 * <div ref={onInteractOutsideOfRef(() => {console.log('Clicked outside')})}/>
 */

export const onInteractOutsideOfRef =
	<ElementType extends HTMLElement>(
		onInteractOutside: (element: ElementType, event: FocusEvent) => void,
	) =>
	(element: ElementType | null) => {
		if (element == null) return () => {};
		const abortController = new AbortController();
		const onInteract = (event: FocusEvent) => {
			if (element.contains(event.target as Node)) return;

			onInteractOutside(element, event);
		};
		document.addEventListener('focusin', onInteract, {
			signal: abortController.signal,
		});
		document.addEventListener('click', onInteract, {
			signal: abortController.signal,
		});
		return () => {
			abortController.abort();
		};
	};
